import React from 'react';

/**
 * ApplicationProgressTracker - Shows step-by-step progress for an application
 * Displays status, documents needed, and actions at each stage
 */
const ApplicationProgressTracker = ({ application, steps, currentStep }) => {
    const defaultSteps = [
        {
            id: 'draft',
            label: 'Application Draft',
            description: 'Initial application submitted',
            icon: '📝'
        },
        {
            id: 'documents',
            label: 'Documents',
            description: 'Upload required documents',
            icon: '📄'
        },
        {
            id: 'review',
            label: 'Under Review',
            description: 'Application being processed',
            icon: '🔍'
        },
        {
            id: 'decision',
            label: 'Decision',
            description: 'Awaiting admission decision',
            icon: '⏳'
        },
        {
            id: 'offer',
            label: 'Offer',
            description: 'Offer received - action needed',
            icon: '🎉'
        }
    ];

    const progressSteps = steps || defaultSteps;

    const getStepIndex = () => {
        if (!currentStep) return 0;
        const idx = progressSteps.findIndex(s => s.id === currentStep);
        return idx >= 0 ? idx : 0;
    };

    const currentIndex = getStepIndex();

    const getStepStatus = (index) => {
        if (index < currentIndex) return 'completed';
        if (index === currentIndex) return 'current';
        return 'upcoming';
    };

    return (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-slate-900">Application Progress</h3>
                    {application?.university && (
                        <p className="text-sm text-slate-500 mt-0.5">{application.university}</p>
                    )}
                </div>
                <span className="px-3 py-1 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-full">
                    {currentIndex + 1} of {progressSteps.length}
                </span>
            </div>

            {/* Horizontal Progress on desktop */}
            <div className="hidden md:block">
                <div className="relative flex items-center justify-between">
                    {/* Background line */}
                    <div className="absolute top-6 left-0 right-0 h-0.5 bg-slate-200"></div>

                    {/* Progress line */}
                    <div
                        className="absolute top-6 left-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
                        style={{ width: `${(currentIndex / (progressSteps.length - 1)) * 100}%` }}
                    ></div>

                    {progressSteps.map((step, idx) => {
                        const status = getStepStatus(idx);
                        return (
                            <div key={step.id} className="relative flex flex-col items-center z-10" style={{ flex: 1 }}>
                                {/* Step circle */}
                                <div className={`
                                    w-12 h-12 rounded-full flex items-center justify-center text-xl mb-3
                                    transition-all duration-300
                                    ${status === 'completed'
                                        ? 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-lg shadow-indigo-200'
                                        : status === 'current'
                                            ? 'bg-white border-2 border-indigo-500 ring-4 ring-indigo-100 shadow-lg'
                                            : 'bg-slate-100 border border-slate-200'
                                    }
                                `}>
                                    {status === 'completed' ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    ) : (
                                        <span className={status === 'upcoming' ? 'opacity-50' : ''}>{step.icon}</span>
                                    )}
                                </div>

                                {/* Step label */}
                                <p className={`
                                    text-sm font-medium text-center
                                    ${status === 'current' ? 'text-indigo-600' : status === 'completed' ? 'text-slate-700' : 'text-slate-400'}
                                `}>
                                    {step.label}
                                </p>

                                {/* Description for current step */}
                                {status === 'current' && (
                                    <p className="text-xs text-slate-500 text-center mt-1 max-w-[120px]">
                                        {step.description}
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Vertical Progress on mobile */}
            <div className="md:hidden space-y-4">
                {progressSteps.map((step, idx) => {
                    const status = getStepStatus(idx);
                    return (
                        <div key={step.id} className="flex items-start gap-4">
                            {/* Step indicator with line */}
                            <div className="flex flex-col items-center">
                                <div className={`
                                    w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0
                                    ${status === 'completed'
                                        ? 'bg-gradient-to-br from-indigo-500 to-purple-500 text-white'
                                        : status === 'current'
                                            ? 'bg-white border-2 border-indigo-500'
                                            : 'bg-slate-100'
                                    }
                                `}>
                                    {status === 'completed' ? '✓' : step.icon}
                                </div>
                                {idx < progressSteps.length - 1 && (
                                    <div className={`w-0.5 h-8 mt-1 ${idx < currentIndex ? 'bg-indigo-500' : 'bg-slate-200'}`}></div>
                                )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 pb-4">
                                <p className={`
                                    font-medium
                                    ${status === 'current' ? 'text-indigo-600' : status === 'completed' ? 'text-slate-700' : 'text-slate-400'}
                                `}>
                                    {step.label}
                                </p>
                                <p className={`text-sm ${status === 'current' ? 'text-slate-600' : 'text-slate-400'}`}>
                                    {step.description}
                                </p>
                                {status === 'current' && step.action && (
                                    <button className="mt-2 text-sm text-indigo-600 font-medium hover:text-indigo-700">
                                        {step.action} →
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ApplicationProgressTracker;
